// ════════════════════════════════════════════════════════════════════════════
// AgendaSemanal — r140
// Calendário semanal/útil do colaborador, com slots de 30 minutos.
// Aceita drag&drop de tarefas (props onDropTarefa).
// ════════════════════════════════════════════════════════════════════════════

import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';

const COR = {
  azul:    '#003399',
  azulBg:  '#eff6ff',
  borda:   '#e8eaed',
  texto:   '#1a1a2e',
  muted:   '#888',
  branco:  '#fff',
  verde:   '#1D9E75',
  verdeBg: '#f0fff4',
  amarelo: '#d97706',
  amlBg:   '#fffbeb',
  vermelho:'#e53935',
  cinzaBg: '#fafbfc',
};

const NOMES_DIAS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

// Slot de 30 minutos das 08:00 às 20:00 = 24 linhas
const HORA_INICIO = 8;
const HORA_FIM    = 20;
const SLOTS       = []; // ["08:00","08:30",..., "19:30"]
for (let h = HORA_INICIO; h < HORA_FIM; h++) {
  SLOTS.push(`${String(h).padStart(2,'0')}:00`);
  SLOTS.push(`${String(h).padStart(2,'0')}:30`);
}
const ALT_SLOT = 22;  // px de altura por slot
const ALT_HORA = ALT_SLOT * 2;

// ─── Helpers de data ──────────────────────────────────────────────────────────
function inicioDaSemana(d, util = true) {
  // Retorna a data da segunda-feira da semana de d
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  const dia = dt.getDay(); // 0=domingo, 1=segunda...
  const offset = dia === 0 ? -6 : 1 - dia; // segunda
  dt.setDate(dt.getDate() + offset);
  return dt;
}
function addDias(d, n) {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n);
  return dt;
}
function fmtData(d) {
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function fmtDataCurta(d) {
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}
function mesmoDia(a, b) {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
}
function ehHoje(d) { return mesmoDia(d, new Date()); }
function ehPassado(d) {
  const hoje = new Date();
  hoje.setHours(0,0,0,0);
  return d < hoje;
}

// Converte ISO/Date → minutos do dia
function minDoDia(d) {
  const dt = new Date(d);
  return dt.getHours() * 60 + dt.getMinutes();
}
// Converte minutos do dia → topo em px relativo ao 08:00
function pxDoMinuto(min) {
  return ((min - HORA_INICIO * 60) / 30) * ALT_SLOT;
}

// ─── Cor do bloco no calendário ───────────────────────────────────────────────
function corBloco(tipo) {
  if (tipo === 'TAREFA_PLANEJADA') return { cor: COR.azul,    bg: COR.azulBg,  borda: COR.azul    };
  if (tipo === 'TAREFA')           return { cor: COR.verde,   bg: COR.verdeBg, borda: COR.verde   };
  if (tipo === 'CALL')             return { cor: '#7c3aed',   bg: '#f3f0ff',   borda: '#7c3aed'   }; // r170: call de negócio
  return                                  { cor: COR.amarelo, bg: COR.amlBg,   borda: COR.amarelo };
}

// ─── Componente: bloco posicionado no calendário ─────────────────────────────
// r148: aceita handlers de drag/redim. Só dispara onClick se mouseUp sem ter
//       movido (threshold 4px). Se for TAREFA_PLANEJADA, suporta arrastar pra
//       mover e arrastar a borda inferior pra redimensionar.
function BlocoAlocacao({ aloc, onClick, onIniciarMover, onIniciarRedim, fantasma }) {
  const ini = new Date(aloc.data_inicio);
  const fim = new Date(aloc.data_fim);
  const topo = pxDoMinuto(minDoDia(ini));
  const altura = Math.max(ALT_SLOT - 2, ((fim - ini) / 1000 / 60 / 30) * ALT_SLOT - 2);
  const c = corBloco(aloc.tipo);

  // r149: planejada E executada podem ser movidas/redimensionadas
  const podeAlterar = aloc.tipo === 'TAREFA_PLANEJADA' || aloc.tipo === 'TAREFA';

  // Estado pra distinguir click de drag
  const mouseDownPos = useRef(null);

  const handleMouseDownBloco = (e) => {
    if (!podeAlterar) return;          // só plan
    if (e.button !== 0) return;        // só botão esquerdo
    e.stopPropagation();
    mouseDownPos.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    onIniciarMover && onIniciarMover(aloc, e);
  };

  const handleMouseDownRedim = (e) => {
    if (!podeAlterar) return;
    if (e.button !== 0) return;
    e.stopPropagation();
    mouseDownPos.current = { x: e.clientX, y: e.clientY, t: Date.now(), redim: true };
    onIniciarRedim && onIniciarRedim(aloc, e);
  };

  const handleClickBloco = (e) => {
    e.stopPropagation();
    // Se vai daria click depois de um drag? Aqui só dispara se mouseDownPos
    // sumiu (foi resetado pelo pai após mouseUp sem movimento).
    onClick && onClick(aloc);
  };

  return (
    <div
      onMouseDown={handleMouseDownBloco}
      onClick={handleClickBloco}
      title={`${aloc.tarefa_titulo || aloc.observacao || '(sem título)'}\n${ini.toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'})} - ${fim.toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'})}${podeAlterar ? '\n(arraste pra mover, borda inferior pra redimensionar)' : ''}`}
      style={{
        position: 'absolute',
        top: `${topo}px`, height: `${altura}px`,
        left: '2px', right: '2px',
        backgroundColor: c.bg,
        borderLeft: `3px solid ${c.borda}`,
        borderRadius: '4px',
        padding: '3px 6px',
        fontSize: '10px',
        overflow: 'hidden',
        cursor: podeAlterar ? 'grab' : 'pointer',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        pointerEvents: 'auto',
        opacity: fantasma ? 0.35 : 1,    // r148: bloco original fica opaco durante drag
        userSelect: 'none',
        display: 'flex', flexDirection: 'column', gap: '1px',
      }}
    >
      {/* r165: layout do bloco em 3 linhas:
          Linha 1: #NÚMERO - Título da tarefa (bold)
          Linha 2: Cliente Fantasia + tag colorida
          Linha 3: 📁 Descrição do projeto (negócio)
          Cada linha só aparece se tiver o dado. Se o bloco for baixo, overflow corta. */}

      {/* Linha 1: #número - título */}
      <div style={{
        fontWeight: '700', color: c.cor, lineHeight: '12px',
        textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
      }}>
        {aloc.tarefa_id
          ? `#${aloc.tarefa_id} - ${aloc.tarefa_titulo || aloc.observacao || '(sem título)'}`
          : (
            <>
              <span style={{ marginRight: '3px' }}>{aloc.tipo === 'CALL' ? '📞' : aloc.tipo === 'TAREFA_PLANEJADA' ? '📅' : '⏱'}</span>
              {aloc.tarefa_titulo || aloc.observacao || '(sem título)'}
            </>
          )}
      </div>

      {/* Linha 2: apenas a tag colorida do cliente (r166: sem nome do cliente) */}
      {aloc.cliente_tag && (
        <div style={{
          lineHeight: '12px',
          display: 'flex', alignItems: 'center',
        }}>
          <span style={{
            padding: '0 5px', borderRadius: '3px',
            fontSize: '9px', fontWeight: '700',
            backgroundColor: aloc.cliente_cor_tag || COR.borda,
            color: '#fff',
            lineHeight: '11px',
            textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}>{aloc.cliente_tag}</span>
        </div>
      )}

      {/* Linha 3: 📁 Descrição do projeto (só se tem negócio) */}
      {aloc.negocio_id && (
        <div style={{
          color: COR.muted || '#666', lineHeight: '12px',
          textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
          display: 'flex', alignItems: 'center', gap: '3px',
        }}>
          <span>📁</span>
          <span style={{
            textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
          }}>{aloc.negocio_descricao || aloc.negocio_id}</span>
        </div>
      )}

      {/* r148: handle de redimensionar (só pra planejada) */}
      {podeAlterar && (
        <div
          onMouseDown={handleMouseDownRedim}
          style={{
            position: 'absolute', left: 0, right: 0, bottom: 0,
            height: '6px',
            cursor: 'ns-resize',
            backgroundColor: 'transparent',
          }}
          title="Arraste pra redimensionar"
        />
      )}
    </div>
  );
}

// ─── Componente: ghost (fantasma) que mostra a posição durante drag/redim ────
function BlocoFantasma({ aloc, dataInicio, dataFim, tipoInteracao }) {
  const ini = new Date(dataInicio);
  const fim = new Date(dataFim);
  const topo = pxDoMinuto(minDoDia(ini));
  const altura = Math.max(ALT_SLOT - 2, ((fim - ini) / 1000 / 60 / 30) * ALT_SLOT - 2);
  const c = corBloco(aloc.tipo);
  const fmtHora = d => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{
      position: 'absolute',
      top: `${topo}px`, height: `${altura}px`,
      left: '2px', right: '2px',
      backgroundColor: c.bg,
      border: `2px dashed ${c.borda}`,
      borderRadius: '4px',
      padding: '3px 6px',
      fontSize: '10px',
      overflow: 'hidden',
      pointerEvents: 'none',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 10,
      userSelect: 'none',
    }}>
      <div style={{ fontWeight: '700', color: c.cor, lineHeight: '12px' }}>
        {tipoInteracao === 'redim' ? '↕' : '✥'} {fmtHora(ini)} - {fmtHora(fim)}
      </div>
      <div style={{ color: COR.texto, lineHeight: '12px',
                    textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
        {aloc.tarefa_titulo || aloc.observacao || '(sem título)'}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════════════════════
function AgendaSemanal({
  alocacoes,             // array de alocações vindo do backend
  onDropTarefa,          // (tarefaId, slotInicio) → chamado quando solta uma tarefa em um slot
  onClickBloco,          // (aloc) → chamado quando clica num bloco existente
  onAlterarBloco,        // r148: (alocacao, dataInicio, dataFim) → mover/redim
  visao,                 // 'util' (seg-sex) | 'semanal' (dom-sab)
  setVisao,
  dataAtual,             // âncora da semana mostrada
  setDataAtual,
  filtros,               // { planejadas, finalizadas, outros }
  setFiltros,
}) {

  const inicio  = useMemo(() => inicioDaSemana(dataAtual), [dataAtual]);
  const diasVis = visao === 'util' ? 5 : 7;
  const dias    = useMemo(() => Array.from({length: diasVis}, (_, i) => addDias(inicio, i)), [inicio, diasVis]);
  const fim     = useMemo(() => addDias(inicio, diasVis), [inicio, diasVis]);

  // Filtra alocações pelos checkboxes
  const alocFiltradas = useMemo(() => {
    return (alocacoes || []).filter(a => {
      if (a.tipo === 'TAREFA_PLANEJADA' && !filtros.planejadas) return false;
      if (a.tipo === 'TAREFA'           && !filtros.finalizadas) return false;
      if (a.tipo !== 'TAREFA_PLANEJADA' && a.tipo !== 'TAREFA' && !filtros.outros) return false;
      return true;
    });
  }, [alocacoes, filtros]);

  // Agrupa por dia (chave = YYYY-MM-DD)
  const porDia = useMemo(() => {
    const m = {};
    alocFiltradas.forEach(a => {
      const dt = new Date(a.data_inicio);
      const k = dt.toISOString().slice(0,10);
      (m[k] = m[k] || []).push(a);
    });
    return m;
  }, [alocFiltradas]);

  // Drag&drop
  const [slotHover, setSlotHover] = useState(null); // { diaIdx, slotIdx }

  // ═══ r148: drag/redim de bloco planejado ═════════════════════════════════
  // interacao = null | { tipo, aloc, dataInicio, dataFim, mouseStartY, mouseStartX, originalIni, originalFim, originalDia, moved }
  //   tipo = 'mover' | 'redim'
  //   moved = true se o cursor moveu > threshold (4px)
  const [interacao, setInteracao] = useState(null);
  const gridRef = useRef(null);     // pra calcular posições absolutas dentro da tabela
  const interacaoRef = useRef(null);
  interacaoRef.current = interacao; // pra acessar dentro de listeners

  // Recebe aloc + evento mouse, prepara interação de "mover"
  const iniciarMover = useCallback((aloc, e) => {
    setInteracao({
      tipo: 'mover',
      aloc,
      dataInicio: aloc.data_inicio,
      dataFim:    aloc.data_fim,
      mouseStartX: e.clientX,
      mouseStartY: e.clientY,
      originalIni: new Date(aloc.data_inicio),
      originalFim: new Date(aloc.data_fim),
      moved: false,
    });
  }, []);

  // Inicia redimensionamento (borda inferior)
  const iniciarRedim = useCallback((aloc, e) => {
    setInteracao({
      tipo: 'redim',
      aloc,
      dataInicio: aloc.data_inicio,
      dataFim:    aloc.data_fim,
      mouseStartX: e.clientX,
      mouseStartY: e.clientY,
      originalIni: new Date(aloc.data_inicio),
      originalFim: new Date(aloc.data_fim),
      moved: false,
    });
  }, []);

  // Listener global: mouseMove + mouseUp
  useEffect(() => {
    if (!interacao) return;

    function handleMove(e) {
      const it = interacaoRef.current;
      if (!it) return;
      const dy = e.clientY - it.mouseStartY;
      const dx = e.clientX - it.mouseStartX;
      const movido = Math.abs(dy) > 4 || Math.abs(dx) > 4;

      if (!movido) return;  // ainda dentro do "click"
      // Marca como movido (pra suprimir o onClick depois)
      if (!it.moved) {
        setInteracao(prev => prev ? { ...prev, moved: true } : prev);
      }

      // Snap em slots de 30 min (= ALT_SLOT px)
      const slotsDelta = Math.round(dy / ALT_SLOT);
      const minDelta = slotsDelta * 30;     // minutos de delta

      if (it.tipo === 'redim') {
        // Mantém início, ajusta fim
        const novoFim = new Date(it.originalFim);
        novoFim.setMinutes(novoFim.getMinutes() + minDelta);
        // Mínimo: 30 min depois do início
        const minFim = new Date(it.originalIni);
        minFim.setMinutes(minFim.getMinutes() + 30);
        if (novoFim < minFim) return;
        // Máximo: 20:00 do mesmo dia
        const maxFim = new Date(it.originalIni);
        maxFim.setHours(HORA_FIM, 0, 0, 0);
        if (novoFim > maxFim) return;
        setInteracao(prev => prev ? { ...prev, dataFim: novoFim.toISOString() } : prev);
      } else {
        // mover: ajusta início e fim mantendo duração
        // Considera tb mudança de dia horizontal (dx)
        const larguraColuna = gridRef.current ? (gridRef.current.querySelector('th[data-dia]')?.offsetWidth || 100) : 100;
        const diaDelta = Math.round(dx / larguraColuna);

        const novoIni = new Date(it.originalIni);
        novoIni.setMinutes(novoIni.getMinutes() + minDelta);
        novoIni.setDate(novoIni.getDate() + diaDelta);

        const dur = it.originalFim.getTime() - it.originalIni.getTime();
        const novoFim = new Date(novoIni.getTime() + dur);

        // Limites: não pode passar de 20:00 nem antes de 08:00 num único dia
        // (não vou impor pra simplificar — backend valida)

        setInteracao(prev => prev ? {
          ...prev,
          dataInicio: novoIni.toISOString(),
          dataFim:    novoFim.toISOString(),
        } : prev);
      }
    }

    function handleUp() {
      const it = interacaoRef.current;
      if (!it) return;
      if (it.moved && onAlterarBloco) {
        // Dispara alteração
        onAlterarBloco(it.aloc, it.dataInicio, it.dataFim);
      }
      setInteracao(null);
    }

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup',   handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup',   handleUp);
    };
  }, [interacao, onAlterarBloco]);

  // Suprime onClick se a interação está marcada como moved
  const aoClicarBloco = useCallback((aloc) => {
    // Se acabou de mover, NÃO dispara onClick (já tratou no mouseUp)
    const it = interacaoRef.current;
    if (it && it.moved) return;
    onClickBloco && onClickBloco(aloc);
  }, [onClickBloco]);
  // ════════════════════════════════════════════════════════════════════════════

  const onDragOver = (e, diaIdx, slotIdx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setSlotHover({ diaIdx, slotIdx });
  };
  const onDropSlot = (e, diaIdx, slotIdx) => {
    e.preventDefault();
    setSlotHover(null);
    const tarefaId = e.dataTransfer.getData('text/tarefa-id');
    if (!tarefaId) return;
    // Calcula a data/hora do slot
    const dt = new Date(dias[diaIdx]);
    const minutos = HORA_INICIO * 60 + slotIdx * 30;
    dt.setHours(Math.floor(minutos / 60), minutos % 60, 0, 0);
    onDropTarefa && onDropTarefa(tarefaId, dt);
  };
  const onDragLeave = () => setSlotHover(null);

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* ─── Barra de controles (visão + navegação + filtros) ──────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
        padding: '10px', backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`, borderRadius: '8px 8px 0 0',
        borderBottom: 'none',
      }}>
        <select value={visao} onChange={e => setVisao(e.target.value)}
                style={{ ...selectStyle, width: '140px' }}>
          <option value="util">Semanal (Útil)</option>
          <option value="semanal">Semanal (7 dias)</option>
        </select>

        <button onClick={() => setDataAtual(new Date())} style={btnSec}>
          📅 Hoje
        </button>
        <button onClick={() => setDataAtual(addDias(dataAtual, -7))} style={btnSec}>‹</button>
        <button onClick={() => setDataAtual(addDias(dataAtual, +7))} style={btnSec}>›</button>

        <span style={{ fontSize: '13px', color: COR.texto, fontWeight: '600' }}>
          {fmtDataCurta(dias[0])} - {fmtDataCurta(dias[dias.length - 1])} de {dias[0].toLocaleDateString('pt-BR', { month: 'long' })}
        </span>

        <div style={{ flex: 1 }} />

        {/* Filtros checkbox */}
        <Check label="Tarefas Agendadas"   checked={filtros.planejadas}  onChange={v => setFiltros(f => ({ ...f, planejadas: v }))} cor={COR.azul} />
        <Check label="Tarefas Finalizadas" checked={filtros.finalizadas} onChange={v => setFiltros(f => ({ ...f, finalizadas: v }))} cor={COR.verde} />
        <Check label="Outros Compromissos" checked={filtros.outros}      onChange={v => setFiltros(f => ({ ...f, outros: v }))} cor={COR.amarelo} />
      </div>

      {/* ─── Grid do calendário ─────────────────────────────────────────── */}
      <div style={{
        backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`, borderRadius: '0 0 8px 8px',
        overflow: 'auto', maxHeight: 'calc(100vh - 280px)',
      }}>
        <table ref={gridRef} style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          {/* Cabeçalho com dias da semana */}
          <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr style={{ backgroundColor: COR.azul }}>
              <th style={thHora}></th>
              {dias.map((d, i) => (
                <th key={i} data-dia={i} style={{
                  ...thDia,
                  backgroundColor: ehHoje(d) ? '#0044bb' : COR.azul,
                }}>
                  <div>{NOMES_DIAS[d.getDay()]}</div>
                  <div style={{ fontSize: '11px', opacity: 0.9 }}>{fmtData(d)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SLOTS.map((horaLabel, slotIdx) => {
              const horaCheia = horaLabel.endsWith(':00');
              return (
                <tr key={slotIdx} style={{ height: `${ALT_SLOT}px` }}>
                  <td style={{
                    ...tdHora,
                    fontWeight: horaCheia ? '600' : '400',
                    fontSize: horaCheia ? '12px' : '11px',
                    color: horaCheia ? COR.texto : COR.muted,
                  }}>{horaLabel}</td>
                  {dias.map((d, diaIdx) => {
                    const isHover = slotHover && slotHover.diaIdx === diaIdx && slotHover.slotIdx === slotIdx;
                    return (
                      <td
                        key={diaIdx}
                        onDragOver={e => onDragOver(e, diaIdx, slotIdx)}
                        onDragLeave={onDragLeave}
                        onDrop={e => onDropSlot(e, diaIdx, slotIdx)}
                        style={{
                          ...tdSlot,
                          borderBottom: horaCheia ? `1px solid ${COR.borda}` : `1px dotted ${COR.borda}`,
                          backgroundColor: isHover ? COR.azulBg : (ehHoje(d) ? '#fbfdff' : COR.branco),
                          position: slotIdx === 0 ? 'relative' : undefined,
                        }}
                      >
                        {/* Os blocos ficam absolutos no primeiro slot do dia */}
                        {slotIdx === 0 && (
                          <div style={{
                            position: 'absolute', top: 0, left: 0, right: 0,
                            height: `${SLOTS.length * ALT_SLOT}px`,
                            pointerEvents: 'none',
                          }}>
                            {/* r144b: pointerEvents none aqui também, pra eventos
                                       passarem direto pro <td> do slot certo.
                                       Os blocos individuais reabilitam (auto). */}
                            <div style={{ position: 'relative', height: '100%', pointerEvents: 'none' }}>
                              {(porDia[d.toISOString().slice(0,10)] || []).map(a => {
                                // r148: se está em interação com esse aloc, deixa transparente
                                //       e mostra o fantasma na posição/duração atual
                                const interagindo = interacao && interacao.aloc.id === a.id && interacao.moved;
                                return (
                                  <BlocoAlocacao
                                    key={a.id}
                                    aloc={a}
                                    onClick={aoClicarBloco}
                                    onIniciarMover={iniciarMover}
                                    onIniciarRedim={iniciarRedim}
                                    fantasma={interagindo}
                                  />
                                );
                              })}
                              {/* r148: ghost — visual feedback durante drag/redim */}
                              {interacao && interacao.moved
                               && new Date(interacao.dataInicio).toISOString().slice(0,10) === d.toISOString().slice(0,10)
                               && (
                                <BlocoFantasma
                                  aloc={interacao.aloc}
                                  dataInicio={interacao.dataInicio}
                                  dataFim={interacao.dataFim}
                                  tipoInteracao={interacao.tipo}
                                />
                               )}
                            </div>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Estilos auxiliares ──────────────────────────────────────────────────────
const selectStyle = {
  height: '32px', padding: '0 8px', border: `1px solid ${COR.borda}`,
  borderRadius: '5px', fontSize: '12px', backgroundColor: COR.branco,
  color: COR.texto, fontFamily: "'Manrope', sans-serif", cursor: 'pointer',
};
const btnSec = {
  height: '32px', padding: '0 12px', border: `1px solid ${COR.borda}`,
  borderRadius: '5px', backgroundColor: COR.branco, color: COR.texto,
  fontSize: '12px', fontWeight: '500', cursor: 'pointer',
  fontFamily: "'Manrope', sans-serif",
};
const thHora = {
  padding: '8px', color: COR.branco, fontWeight: '700', fontSize: '13px',
  width: '70px', minWidth: '70px', textAlign: 'center',
  borderRight: `1px solid ${COR.borda}`,
};
const thDia = {
  padding: '8px', color: COR.branco, fontWeight: '700', fontSize: '13px',
  textAlign: 'center',
  borderRight: `1px solid rgba(255,255,255,0.15)`,
};
const tdHora = {
  textAlign: 'center', borderRight: `1px solid ${COR.borda}`,
  backgroundColor: COR.cinzaBg, padding: '0',
};
const tdSlot = {
  borderRight: `1px solid ${COR.borda}`,
  padding: '0', verticalAlign: 'top',
};

// ─── Subcomponente: checkbox custom ──────────────────────────────────────────
function Check({ label, checked, onChange, cor }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px',
                    cursor: 'pointer', color: COR.texto, userSelect: 'none' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
             style={{ accentColor: cor, cursor: 'pointer' }} />
      <span style={{ color: cor, fontWeight: '600' }}>●</span>
      {label}
    </label>
  );
}

export default AgendaSemanal;
